/**
 * ProximityNotificationStack
 *
 * Unified floating carousel that merges three notification types:
 *   1. Entity proximity prompt (nearby entity quick-confirm / Still Here)
 *   2. Onboarding checklist
 *   3. City proximity banners
 *
 * Entity prompt is always seeded first (highest priority).
 * Ghost peek strips below the active card are tappable to cycle the deck.
 * Dismiss (×) on each card permanently removes that item for the session.
 *
 * @atomic @idempotent
 */
import { useState, useRef, useCallback, memo } from 'react'
import { MapPinIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import useNearbyEntityPrompt from '../../hooks/useNearbyEntityPrompt'
import useCityProximity from '../../hooks/useCityProximity'
import { useEnrichmentNudges } from '../../hooks/useEnrichmentNudges'
import NearbyEntityPrompt from './NearbyEntityPrompt'
import CityProximityBanner from '../CityProximityBanner'
import OnboardingProgress, { isOnboardingSessionClosed } from '../onboarding/OnboardingProgress'
import EnrichmentNudgeCard from './EnrichmentNudgeCard'

const PEEK = 10   // height (px) of each peeking ghost strip
const INSET = 6   // extra horizontal inset per depth level
const CARD_PAD = 12 // horizontal margin of the active card

const ProximityNotificationStack = memo(function ProximityNotificationStack({
  userLocation,
  locationSource,
  entities,
  isImmersiveMode,
  onTabChange,
  navigate,
}) {
  const {
    nearbyEntity,
    nearbyCount: entityQueueCount,
    queueIndex: entityQueueIndex,
    dismiss: dismissEntity,
  } = useNearbyEntityPrompt(userLocation, locationSource, entities)

  const {
    nearbyCities,
    dismiss: dismissCity,
    resetDismissed,
    ready: cityReady,
  } = useCityProximity()

  const { nudges: enrichmentNudges, dismiss: dismissEnrichment } = useEnrichmentNudges()

  const hadCitiesRef = useRef(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [onboardingOpen, setOnboardingOpen] = useState(() => !isOnboardingSessionClosed())

  if (cityReady && nearbyCities.length > 0) hadCitiesRef.current = true

  // Build ordered items array: entity → onboarding → cities
  const items = []
  if (nearbyEntity) items.push({ type: 'entity', key: String(nearbyEntity.id || 'entity') })
  if (onboardingOpen) items.push({ type: 'onboarding', key: 'onboarding' })
  if (cityReady) {
    nearbyCities.slice(0, 3).forEach(({ city, distanceKm }) =>
      items.push({ type: 'city', key: city.slug, city, distanceKm })
    )
  }
  // Enrichment nudges: lowest priority — shown after entity/onboarding/city cards
  enrichmentNudges.forEach(nudge =>
    items.push({ type: 'enrichment', key: `enrich-${nudge.sightingId}`, nudge })
  )

  const total = items.length
  // Clamp inline — handles items disappearing after dismiss without a useEffect
  const clampedIndex = total > 0 ? Math.min(activeIndex, total - 1) : 0

  const cycleNext = useCallback((e) => {
    if (e) { e.stopPropagation(); e.preventDefault() }
    setActiveIndex(ci => (ci + 1) % total)
  }, [total])

  // Nothing to show — offer restore chip if cities were previously visible
  if (total === 0) {
    if (cityReady && hadCitiesRef.current) {
      return (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[700] px-3">
          <button
            onClick={resetDismissed}
            onTouchEnd={resetDismissed}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs text-gray-500 shadow-sm hover:text-gray-700 hover:border-gray-300 transition-colors"
          >
            <MapPinIcon className="h-3.5 w-3.5 text-primary" />
            Show nearby city pages
          </button>
        </div>
      )
    }
    return null
  }

  // Rotate so clampedIndex is first — rest follow in order
  const displayItems = [...items.slice(clampedIndex), ...items.slice(0, clampedIndex)]
  const activeItem = displayItems[0]

  return (
    <div
      className="absolute top-3 left-1/2 -translate-x-1/2 z-[700] w-full max-w-sm"
      style={{ paddingBottom: (total - 1) * PEEK }}
    >
      {/* Ghost peek strips — tappable to cycle the deck */}
      {displayItems.slice(1).map((item, relI) => {
        const level = relI + 1
        const inset = CARD_PAD + level * INSET
        const bottomOffset = (total - 1 - level) * PEEK
        return (
          <div
            key={`peek-${item.key}`}
            onClick={cycleNext}
            onTouchEnd={cycleNext}
            className="absolute bg-white border border-gray-100 rounded-xl shadow-sm cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors"
            style={{
              left: inset,
              right: inset,
              bottom: bottomOffset,
              height: PEEK + 4,
              zIndex: 30 - level * 10,
            }}
          />
        )
      })}

      {/* Active card */}
      <div className="relative z-30 mx-3 pointer-events-auto">
        {activeItem.type === 'onboarding' ? (
          <OnboardingProgress
            onDismiss={() => setOnboardingOpen(false)}
            onTabChange={onTabChange}
          />
        ) : activeItem.type === 'entity' ? (
          <NearbyEntityPrompt
            entity={nearbyEntity}
            onDismiss={dismissEntity}
            nearbyCount={total > 1 ? total : entityQueueCount}
            queueIndex={total > 1 ? clampedIndex + 1 : entityQueueIndex}
            isImmersiveMode={isImmersiveMode}
            onTabChange={onTabChange}
            navigate={navigate}
          />
        ) : activeItem.type === 'enrichment' ? (
          <EnrichmentNudgeCard
            nudge={activeItem.nudge}
            onDismiss={dismissEnrichment}
          />
        ) : (
          <CityProximityBanner
            city={activeItem.city}
            distanceKm={activeItem.distanceKm}
            onDismiss={() => dismissCity(activeItem.city.slug)}
          />
        )}

        {/* Cycle pill — only when more cards are stacked behind */}
        {total > 1 && (
          <button
            onClick={cycleNext}
            onTouchEnd={cycleNext}
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 bg-white border border-gray-200 rounded-full pl-2.5 pr-2 py-0.5 shadow-sm text-xs text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors pointer-events-auto"
            title="Show next notification"
          >
            <span className="tabular-nums font-medium">{clampedIndex + 1}/{total}</span>
            <ChevronDownIcon className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  )
})

export default ProximityNotificationStack
